import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { observer } from 'mobx-react-lite';

import { DI_TOKENS, queryKeys } from '@constants';
import { ChatPage as UIChatPage, useChatAttachments } from '@features/chat';
import {
  komplyanceAiApiService,
  useConversationById,
  useConversationMessages,
  useRenameConversation,
  useSendFeedback,
} from '@features/komplyance-ai';
import { useInjection } from '@hooks';
import { useTrans } from '@libs/i18n';
import {
  type FeedbackRating,
  type KomplyanceAiInvalidChunk,
  type KomplyanceAiMessageChunk,
  type KomplyanceAiMessageEndChunk,
  type KomplyanceAiMessageEndRetrieverResource,
  type KomplyanceAiWorkflowFinishedChunk,
  type SendMessageStream,
} from '@libs/types';
import { removeEmojis } from '@libs/utils';
import { ConversationsHistoryStore, StreamingStore } from '@stores';

import { NewChatPage } from './NewChatPage';

// Structured chat error for rate limit, circuit breaker, and retryable failures
export type ChatError = {
  message: string;
  code?: string;
  recoverable?: boolean;
  retryAfter?: number;
};

// Internal message type for state management
export type komplyanceAiChatMessage = {
  id: string;
  type: 'user' | 'ai';
  content: string;
  createdAt: Date;
  references: KomplyanceAiMessageEndRetrieverResource[];
  messageId?: string;
  feedback?: {
    rating: FeedbackRating;
    updatedAt: Date;
  };
  files?: Array<{
    fileName: string;
    mimeType?: string;
    docId?: string;
    s3Key?: string;
  }>;
  streamingStage?: string;
  streamingStageMessage?: string;
  gradingCurrent?: number;
  gradingTotal?: number;
};

export const ChatPage: FC<{ conversationId: string }> = observer(({ conversationId }) => {

  /* ========================================================================
     Translations
     ======================================================================== */
  const { t } = useTrans();
  const location = useLocation();
  const navigationState = location.state as
    | {
        initialMessage?: string;
        initialDeepResearchEnabled?: boolean;
        initialFiles?: komplyanceAiChatMessage['files'];
      }
    | null;
  const initialMessage = navigationState?.initialMessage;
  const initialDeepResearchEnabled = navigationState?.initialDeepResearchEnabled ?? false;
  const initialFiles = navigationState?.initialFiles;

  /* ========================================================================
     Stores
     ======================================================================== */
  const streamingStore = useInjection<StreamingStore>(DI_TOKENS.StreamingStore);
  const conversationsHistoryStore = useInjection<ConversationsHistoryStore>(
    DI_TOKENS.ConversationsHistoryStore,
  );
  const currentConversation = conversationsHistoryStore.currentConversation;


  /* ========================================================================
  State management
  ======================================================================== */
  const [messages, setMessages] = useState<komplyanceAiChatMessage[]>(() => {
    if (initialMessage) {
      return [{
        id: 'user-initial',
        type: 'user',
        content: initialMessage,
        createdAt: new Date(),
        references: [],
        files: initialFiles,
      }];
    }
    const pending = streamingStore.getPendingUserMessage(conversationId);
    if (pending) {
      return [{ id: 'user-pending', type: 'user', content: pending.content, createdAt: pending.createdAt, references: [] }];
    }
    return [];
  });
  const [chatInputValue, setChatchatInputValue] = useState('');
  const [isStreaming, setIsStreaming] = useState(() =>
    streamingStore.isStreamActive(conversationId),
  );
  const [chatError, setChatError] = useState<ChatError | null>(null);
  const [rateLimitCountdown, setRateLimitCountdown] = useState(0);
  const lastSentMessageRef = useRef<SendMessageStream | null>(null);
  const [currentStreamingMessage, setCurrentStreamingMessage] = useState(() =>
    streamingStore.getStreamingMessage(conversationId),
  );
  const [currentStreamingReferences, setCurrentStreamingReferences] = useState<KomplyanceAiMessageEndRetrieverResource[]>(
    () => streamingStore.getStreamingReferences(conversationId),
  );
  const [streamingStage, setStreamingStage] = useState(
    () => streamingStore.getStreamingStage(conversationId).stage,
  );
  const [streamingStageMessage, setStreamingStageMessage] = useState(
    () => streamingStore.getStreamingStage(conversationId).stageMessage,
  );
  const [gradingCurrent, setGradingCurrent] = useState<number | undefined>(undefined);
  const [gradingTotal, setGradingTotal] = useState<number | undefined>(undefined);
  const [feedbackSubmitting, setFeedbackSubmitting] = useState<Map<string, boolean>>(new Map());
  const [editedChatName, setEditedChatName] = useState<string>('');
  const [isEditingChatName, setisEditingChatName] = useState<boolean>(false);
  // Deep Research mode — routes the send to the quality-rubric-gated endpoint.
  // Persists across sends within this conversation view; resets when the page unmounts.
  const [deepResearchEnabled, setDeepResearchEnabled] = useState<boolean>(
    initialDeepResearchEnabled,
  );
  const queryClient = useQueryClient();

  const handleToggleDeepResearch = useCallback(() => {
    setDeepResearchEnabled((prev) => !prev);
  }, []);

  /* ========================================================================
     Document Upload
     ======================================================================== */
  const attachments = useChatAttachments({ conversationId });

  /* ========================================================================
     Queries
     ======================================================================== */
  const renameChatMutation = useRenameConversation();

  const {
    data: currentConversationData,
    isLoading: isLoadingCurrentConversation,
    isError: isErrorGettingConversation,
    refetch: refetchConversation,
  } = useConversationById(conversationId);

  const {
    data: messagesData,
    isLoading: isLoadingMessages,
    refetch: refetchMessages,
    isError: isErrorGettingMessages,
  } = useConversationMessages(conversationId || '');

  /* ========================================================================
     Refs
     ======================================================================== */
  const endOfPageRef = useRef<HTMLDivElement | null>(null);
  const bufferRef = useRef(streamingStore.getStreamingMessage(conversationId));
  const streamingReferencesRef = useRef<KomplyanceAiMessageEndRetrieverResource[]>([]);
  const stopRefetchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const id = setInterval(() => { setCurrentStreamingMessage(bufferRef.current) }, 100);
    return () => clearInterval(id);
  }, []);

  useEffect(() => () => { if (stopRefetchTimerRef.current) clearTimeout(stopRefetchTimerRef.current); }, []);

  /* ========================================================================
     Load conversation messages when data is fetched
     ======================================================================== */
  useEffect(() => {
    if (messagesData?.data && messagesData.data.length > 0 && conversationId) {
      const loadedMessages: komplyanceAiChatMessage[] = [];
      messagesData.data.forEach((msg) => {
        loadedMessages.push({
          id: `user-${msg.id}`,
          type: 'user',
          content: msg.query,
          createdAt: new Date(msg.created_at * 1000),
          references: [],
          files: msg.message_files?.length
            ? msg.message_files.map((f) => ({
              fileName: f.file_name ?? f.fileName ?? '',
              mimeType: f.mime_type ?? f.mimeType,
              docId: f.doc_id,
              s3Key: f.s3_key,
            }))
            : undefined,
        });

        if (msg.answer) {
          loadedMessages.push({
            id: `ai-${msg.id}`,
            type: 'ai',
            content: msg.answer,
            createdAt: new Date(msg.created_at * 1000),
            references: msg.retriever_resources,
            messageId: msg.id,
            feedback: msg.feedback
              ? { rating: msg.feedback.rating, updatedAt: new Date(msg.created_at * 1000) }
              : undefined,
          });
        }
      });
      setMessages(loadedMessages);
    }
  }, [messagesData, conversationId]);

  /* ========================================================================
     Streaming callbacks
     ======================================================================== */
  const handleStreamChunk = useCallback((chunk: KomplyanceAiMessageChunk) => {
    bufferRef.current += chunk.answer;
  }, []);

  const handleStreamDocuments = useCallback((chunk: KomplyanceAiMessageEndChunk) => {
    const documents = chunk.metadata?.retriever_resources || [];
    streamingReferencesRef.current = documents;
    setCurrentStreamingReferences(documents);
  }, []);

  const handleStreamError = useCallback((chunk: KomplyanceAiInvalidChunk) => {
    const isRateLimit = chunk.code === 'komplyance_AI.RATE_LIMIT_EXCEEDED';
    const isCircuitOpen = chunk.code === 'LLM_CIRCUIT_OPEN' || chunk.code === 'komplyance_AI.CIRCUIT_BREAKER_OPEN';

    setChatError({
      message: isRateLimit
        ? (t('chat.errors.rateLimitExceeded', { seconds: chunk.retryAfter || 30 }) as string)
        : isCircuitOpen
          ? (t('chat.errors.serviceUnavailable') as string)
          : chunk.err || (t('chat.errors.genericError') as string),
      code: chunk.code,
      recoverable: chunk.recoverable ?? !isRateLimit,
      retryAfter: chunk.retryAfter,
    });

    if (isRateLimit && chunk.retryAfter) {
      setRateLimitCountdown(chunk.retryAfter);
    }

    setIsStreaming(false);
    setStreamingStage('');
    setStreamingStageMessage('');
    setGradingCurrent(undefined);
    setGradingTotal(undefined);
    streamingStore.clearPendingUserMessage(conversationId);
  }, [t, conversationId, streamingStore]);

  const handleStreamComplete = useCallback(
    async (chunk: KomplyanceAiWorkflowFinishedChunk) => {
      // Prefer the answer the backend sends on completion over the buffer we
      // accumulated from the message chunks. They are the same text except that
      // the final one has been through `fix_merged_table_rows` and is what was
      // persisted, so adopting it is what makes a table the model emitted as a
      // single merged line render as a table instead of a row of pipes. The
      // buffer remains the fallback: a stream that dies mid-flight is finalized
      // through a synthesized completion carrying an empty answer, and there the
      // partial text the user already watched arrive is all we have.
      const finalAnswer = chunk.data?.outputs?.answer?.trim()
        ? chunk.data.outputs.answer
        : bufferRef.current;

      const newMessage: komplyanceAiChatMessage = {
        id: `ai-${Date.now()}`,
        type: 'ai',
        content: finalAnswer,
        createdAt: new Date(),
        references: streamingReferencesRef.current,
      };
      setMessages((prev) => [...prev, newMessage]);
      setCurrentStreamingMessage('');
      bufferRef.current = '';
      streamingReferencesRef.current = [];
      setIsStreaming(false);
      setStreamingStage('');
      setStreamingStageMessage('');
      setCurrentStreamingReferences([]);
      if (conversationId) {
        streamingStore.clearPendingUserMessage(conversationId);
        refetchMessages();
        queryClient.invalidateQueries({ queryKey: queryKeys.komplyanceAi.conversations });
        queryClient.invalidateQueries({ queryKey: queryKeys.komplyanceAi.conversationById(conversationId) });
      }
    },
    [conversationId, refetchMessages, queryClient, streamingStore],
  );

  /* ========================================================================
     Select conversation when data is fetched
     ======================================================================== */
  useEffect(() => {
    if (!conversationId) return;

    if (!currentConversationData || currentConversationData?.data.length === 0) {
      conversationsHistoryStore.deselectConversation();
    } else {
      const rawName = currentConversationData?.data[0]?.name || '';
      const cleanName = removeEmojis(rawName);

      conversationsHistoryStore.selectConversation({
        id: conversationId,
        name: cleanName,
        inputs: currentConversationData?.data[0]?.inputs || {},
        status: currentConversationData?.data[0]?.status || 'active',
        created_at: currentConversationData?.data[0]?.created_at || Date.now(),
        updated_at: currentConversationData?.data[0]?.updated_at || Date.now(),
      });

      if (cleanName !== rawName && cleanName.trim()) {
        renameChatMutation.mutate(
          { conversationId, data: { name: cleanName } },
          {
            onSuccess: (updatedConversation) => {
              conversationsHistoryStore.selectConversation({
                id: conversationId,
                name: updatedConversation.name,
                inputs: updatedConversation.inputs,
                status: updatedConversation.status,
                created_at: updatedConversation.created_at,
                updated_at: updatedConversation.updated_at,
              });
            },
          },
        );
      }
    }

    return () => { conversationsHistoryStore.deselectConversation(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId, currentConversationData, conversationsHistoryStore]);

  /* ========================================================================
     StreamingStore subscription
     ======================================================================== */
  useEffect(() => {
    if (!conversationId) return;

    const unsubscribe = streamingStore.subscribe(conversationId, {
      // Re-activate streaming state if the component mounted before workflow_started arrived
      onMetadata: () => setIsStreaming(true),
      onChunk: handleStreamChunk,
      onDocuments: handleStreamDocuments,
      onError: handleStreamError,
      onComplete: handleStreamComplete,
      onFileIngestProgress: (chunk) => {
        setStreamingStage(chunk.data.stage);
        setStreamingStageMessage(chunk.data.message);
      },
      onAssessmentStarted: (chunk) => {
        setStreamingStage('assessment_started');
        setStreamingStageMessage(chunk.data.file_name);
      },
      onAssessmentProgress: (chunk) => {
        setStreamingStage(chunk.data.stage);
        setStreamingStageMessage(chunk.data.message);
        if (chunk.data.stage === 'grading' && chunk.data.current !== undefined && chunk.data.total !== undefined) {
          setGradingCurrent(chunk.data.current);
          setGradingTotal(chunk.data.total);
        }
      },
      onAssessmentCompleted: () => {
        setStreamingStage('assessment_completed');
        setStreamingStageMessage('');
      },
      onStepProgress: (chunk) => {
        setStreamingStage(chunk.data.stage);
        setStreamingStageMessage(chunk.data.message);
      },
    });

    return () => { unsubscribe(); };
  }, [
    conversationId,
    streamingStore,
    handleStreamChunk,
    handleStreamDocuments,
    handleStreamError,
    handleStreamComplete,
  ]);

  /* ========================================================================
     Display messages
     ======================================================================== */
  const displayMessages = useMemo(() => {
    const allMessages = [...messages];
    if (isStreaming) {
      allMessages.push({
        id: 'streaming',
        type: 'ai',
        content: currentStreamingMessage,
        createdAt: new Date(),
        references: currentStreamingReferences,
        feedback: undefined,
        streamingStage,
        streamingStageMessage,
        gradingCurrent,
        gradingTotal,
      });
    }
    return allMessages;
  }, [messages, isStreaming, currentStreamingMessage, currentStreamingReferences, streamingStage, streamingStageMessage, gradingCurrent, gradingTotal]);

  /* ========================================================================
     Handlers
     ======================================================================== */
  const handleSendMessage = useCallback(async () => {
    if (!chatInputValue.trim() && attachments.files.length === 0) return;
    if (isStreaming) return;

    const { document } = attachments;

    const userMessage: komplyanceAiChatMessage = {
      id: `user-${Date.now()}`,
      type: 'user',
      content: chatInputValue.trim(),
      createdAt: new Date(),
      references: [],
      files: document
        ? [{
          fileName: document.fileName,
          mimeType: document.mimeType,
          docId: document.docId,
          s3Key: document.s3Key,
        }]
        : undefined,
    };

    setMessages((prev) => [...prev, userMessage]);
    const messageToSend = chatInputValue.trim();
    streamingStore.setPendingUserMessage(conversationId, messageToSend, userMessage.createdAt);
    setChatchatInputValue('');
    attachments.clear();
    setIsStreaming(true);
    setChatError(null);
    setCurrentStreamingMessage('');
    setStreamingStage('');
    setStreamingStageMessage('');
    setGradingCurrent(undefined);
    setGradingTotal(undefined);
    bufferRef.current = '';
    setCurrentStreamingReferences([]);

    const messageData: SendMessageStream = {
      message: messageToSend,
      conversationId,
      document,
      deepResearch: deepResearchEnabled,
    };

    lastSentMessageRef.current = messageData;

    try {
      await komplyanceAiApiService.sendMessage(messageData);
    } catch (error) {
      console.error('[ChatPage] Error sending message:', error);
      setChatError({
        message: error instanceof Error ? error.message : (t('chat.errors.genericError') as string),
        recoverable: true,
      });
      setIsStreaming(false);
    }
  }, [chatInputValue, attachments, isStreaming, conversationId, deepResearchEnabled, t, streamingStore]);

  // Rate limit countdown timer
  useEffect(() => {
    if (rateLimitCountdown <= 0) return;
    const timer = setInterval(() => {
      setRateLimitCountdown((prev) => {
        if (prev <= 1) {
          setChatError(null);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [rateLimitCountdown]);

  const handleRetryMessage = useCallback(async () => {
    if (!lastSentMessageRef.current) return;
    setChatError(null);
    setIsStreaming(true);
    setCurrentStreamingMessage('');
    bufferRef.current = '';
    setCurrentStreamingReferences([]);

    // Re-send the same payload, but refresh `conversationId` to whatever the
    // page currently has. The first send may have been made with
    // `conversationId: null` (new conversation); by the time the user clicks
    // retry, the backend has usually assigned one via `workflow_started`, and
    // the SSE listeners are wired to that id. If we retried with the stale
    // null id, a second new conversation would be spawned and its stream
    // events would never reach this component's listeners.
    const retryPayload: SendMessageStream = {
      ...lastSentMessageRef.current,
      conversationId: conversationId ?? lastSentMessageRef.current.conversationId,
    };
    lastSentMessageRef.current = retryPayload;

    try {
      await komplyanceAiApiService.sendMessage(retryPayload);
    } catch (error) {
      console.error('[ChatPage] Retry failed:', error);
      setChatError({
        message: error instanceof Error ? error.message : (t('chat.errors.genericError') as string),
        recoverable: true,
      });
      setIsStreaming(false);
    }
  }, [conversationId, t]);

  const handleStopGeneration = useCallback(async () => {
    if (!isStreaming || !conversationId) return;

    const stream = streamingStore.streams.get(conversationId);
    if (!stream?.taskId) return;

    const partialContent = bufferRef.current;
    const partialReferences = streamingReferencesRef.current;

    try {
      await komplyanceAiApiService.abortStream(conversationId, stream?.taskId);
    } catch (error) {
      console.error(`Error generating message for ${conversationId}`, error);
    }

    if (partialContent.trim()) {
      setMessages((prev) => [
        ...prev,
        {
          id: `ai-${Date.now()}`,
          type: 'ai' as const,
          content: partialContent,
          createdAt: new Date(),
          references: partialReferences,
        },
      ]);
    }

    bufferRef.current = '';
    streamingReferencesRef.current = [];
    setCurrentStreamingMessage('');
    setCurrentStreamingReferences([]);
    setIsStreaming(false);
    setStreamingStage('');
    setStreamingStageMessage('');
    setGradingCurrent(undefined);
    setGradingTotal(undefined);
    streamingStore.clearPendingUserMessage(conversationId);

    refetchMessages();

    if (stopRefetchTimerRef.current) clearTimeout(stopRefetchTimerRef.current);
    stopRefetchTimerRef.current = setTimeout(() => {
      refetchMessages();
      queryClient.invalidateQueries({ queryKey: queryKeys.komplyanceAi.conversations });
      queryClient.invalidateQueries({ queryKey: queryKeys.komplyanceAi.conversationById(conversationId) });
      stopRefetchTimerRef.current = null;
    }, 2000);
  }, [conversationId, streamingStore, isStreaming, refetchMessages, queryClient]);

  const sendFeedbackMutation = useSendFeedback();

  const handleFeedback = useCallback(
    async (localId: string, rating: FeedbackRating) => {
      const message = messages.find((msg) => msg.id === localId);
      const messageId = message?.messageId;
      if (!messageId) return;

      // Keyed by `localId` because that is what the render side looks up
      // (`feedbackSubmitting.get(message.id)`). Keyed by the server `messageId`
      // the lookup never hit, so the buttons stayed enabled while the request
      // was in flight and a second click fired a duplicate.
      setFeedbackSubmitting((prev) => {
        const newMap = new Map(prev);
        newMap.set(localId, true);
        return newMap;
      });

      try {
        await sendFeedbackMutation.mutateAsync({ messageId, rating });
        // Match on `localId`, the same key the lookup above used. `messageId` is
        // the server's id; a message's local `id` is `ai-${server id}`, so
        // comparing against it never matched and the rating only appeared after
        // a refetch reloaded it from the server.
        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === localId
              ? { ...msg, feedback: { rating, updatedAt: new Date() } }
              : msg,
          ),
        );
      } catch (error) {
        console.error('Feedback submission failed:', error);
      } finally {
        setFeedbackSubmitting((prev) => {
          const newMap = new Map(prev);
          newMap.set(localId, false);
          return newMap;
        });
      }
    },
    [sendFeedbackMutation, messages],
  );

  const handleSaveChatName = useCallback(() => {
    if (!editedChatName.trim()) return;
    const newChatName = editedChatName.trim();
    renameChatMutation.mutate(
      { conversationId, data: { name: newChatName } },
      {
        onSuccess: (updatedConversation) => {
          setEditedChatName('');
          setisEditingChatName(false);
          conversationsHistoryStore.selectConversation({
            id: conversationId,
            name: updatedConversation.name,
            inputs: updatedConversation.inputs,
            status: updatedConversation.status,
            created_at: updatedConversation.created_at,
            updated_at: updatedConversation.updated_at,
          });
        },
        onError: (error) => { console.error('Error occured', error); },
      },
    );
  }, [conversationId, editedChatName, renameChatMutation, conversationsHistoryStore]);

  const handleStopEditingChatName = useCallback(() => {
    setisEditingChatName(false);
    setEditedChatName('');
  }, []);

  const handleStartEditingChatName = useCallback(() => {
    setEditedChatName(currentConversation?.name || '');
    setisEditingChatName(true);
  }, [currentConversation]);

  /* ========================================================================
     Loading / error states
     ======================================================================== */
  const isInitialLoading =
    (isLoadingCurrentConversation || isLoadingMessages) && !isStreaming && messages.length === 0;

  const hasLoadError =
    !isLoadingCurrentConversation &&
    !isLoadingMessages &&
    (isErrorGettingConversation || isErrorGettingMessages);

  if (!conversationId) return <NewChatPage />;

  return (
    <UIChatPage
      messages={displayMessages}
      conversationId={conversationId}
      chatInputValue={chatInputValue}
      isLoadingMessages={isStreaming || isLoadingMessages || isLoadingCurrentConversation}
      isInitialLoading={isInitialLoading}
      refetchConversation={refetchConversation}
      refetchMessages={refetchMessages}
      hasLoadError={hasLoadError}
      feedbackSubmitting={feedbackSubmitting}
      chatError={chatError}
      rateLimitCountdown={rateLimitCountdown}
      onRetryMessage={handleRetryMessage}
      onChatInputChange={setChatchatInputValue}
      onSendMessage={handleSendMessage}
      onStopGeneration={handleStopGeneration}
      onSendFeedback={handleFeedback}
      chatName={currentConversation?.name || ''}
      onSaveChatName={handleSaveChatName}
      onChatNameInputChange={setEditedChatName}
      isLoadingChatNameSaving={renameChatMutation.isPending}
      editedChatName={editedChatName}
      isEditingChatName={isEditingChatName}
      onStopEditingChatName={handleStopEditingChatName}
      onStartEditingChatName={handleStartEditingChatName}
      endOfPageRef={endOfPageRef}
      attachments={attachments}
      deepResearchEnabled={deepResearchEnabled}
      onDeepResearchToggle={handleToggleDeepResearch}
    />
  );
});
